import React from "react";

// import { EvaluationFormErrors } from "../validation/form-validations/Evaluation-validation";
import Eval_InterviewerBlock from "./Eval_InterviewerBlock";
import { EvaluationFormErrors } from "../validation/form-validations/Interview-validation";

export interface Interviewer {
  interviewer_name: string;
  interview_datetime: string;
  selection_criteria: string;
  rating_technical_knowledge: string;
  rating_functional_qualification: string;
  rating_communication_skills: string;
  rating_confidence_level: string;
  rating_body_language: string;
  recommendation: string;
  remarks: string;
}

export interface EvaluationFormData {
  interviewer1: Interviewer;
  interviewer2: Interviewer;
  hrHead: Interviewer;
}

// ─── constants (unchanged) ────────────────────────────────────────────────────
const ratingScale = [
  {
    score: 1,
    behaviour: "Did not observe desired behaviours",
    result: "Not Recommended",
  },
  {
    score: 2,
    behaviour: "Observed desired behaviours somewhat",
    result: "Not Recommended",
  },
  {
    score: 3,
    behaviour: "Observed desired behaviours significantly",
    result: "Hold",
  },
  {
    score: 4,
    behaviour: "Observed desired behaviours fully",
    result: "Recommended",
  },
];

const interviewerBlocks: {
  label: string;
  prefix: keyof Pick<
    EvaluationFormData,
    "interviewer1" | "interviewer2" | "hrHead"
  >;
}[] = [
  { label: "Interviewer 1", prefix: "interviewer1" },
  { label: "Interviewer 2", prefix: "interviewer2" },
  { label: "HR Head/HR", prefix: "hrHead" },
];

// ─── payload builder — maps formData → API shape ──────────────────────────────
export const toApiPayload = (formData: EvaluationFormData) => {
  const typeMap = {
    interviewer1: "Interviewer1",
    interviewer2: "Interviewer2",
    hrHead: "HR Head",
  };
  return interviewerBlocks.map(({ prefix }) => ({
    interviewer_type: typeMap[prefix],
    interviewer_name: formData[prefix].interviewer_name,
    interview_datetime: formData[prefix].interview_datetime,
    selection_criteria: formData[prefix].selection_criteria,
    rating_technical_knowledge: Number(
      formData[prefix].rating_technical_knowledge,
    ),
    rating_functional_qualification: Number(
      formData[prefix].rating_functional_qualification,
    ),
    rating_communication_skills: Number(
      formData[prefix].rating_communication_skills,
    ),
    rating_confidence_level: Number(formData[prefix].rating_confidence_level),
    rating_body_language: Number(formData[prefix].rating_body_language),
    recommendation: formData[prefix].recommendation,
    remarks: formData[prefix].remarks,
  }));
};

// ─── Rating Scale ─────────────────────────────────────────────────────────────
const RatingScale = () => (
  <div className="mb-4">
    <p
      className="fw-semibold mb-2"
      style={{ fontSize: "0.85rem", color: "#111827" }}
    >
      Rating Scale
    </p>
    <table className="form-table" style={{ maxWidth: 600 }}>
      <tbody>
        {ratingScale.map((r) => (
          <tr key={r.score}>
            <td style={{ width: 40, textAlign: "center" }}>{r.score}</td>
            <td>{r.behaviour}</td>
            <td style={{ fontWeight: 500, color: "#111827" }}>{r.result}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const emptyInterviewer = (): Interviewer => ({
  interviewer_name: "",
  interview_datetime: "",
  selection_criteria: "",
  rating_technical_knowledge: "",
  rating_functional_qualification: "",
  rating_communication_skills: "",
  rating_confidence_level: "",
  rating_body_language: "",
  recommendation: "",
  remarks: "",
});

export const evaluationInitialData: EvaluationFormData = {
  interviewer1: emptyInterviewer(),
  interviewer2: emptyInterviewer(),
  hrHead: emptyInterviewer(),
};

interface EvaluationProps {
  formData: EvaluationFormData;
  setFormData: React.Dispatch<React.SetStateAction<EvaluationFormData>>;
  errors: EvaluationFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<EvaluationFormErrors>>;
}

const EvaluationSheet = ({
  formData,
  setFormData,
  errors,
  setErrors,
}: EvaluationProps) => {
  const handleError = (name: string) =>
    setErrors((prev: any) => {
      const [prefix, field] = name.split(".");
      return {
        ...prev,
        [prefix]: { ...prev[prefix], [field]: undefined },
      };
    });

  const handleDateChange = (name: string, value: string) => {
    const [datePart, timePart] = value.split(" ");
    const [day, month, year] = datePart.split("-");
    const formatted = `${year}-${month}-${day} ${timePart ?? "00:00:00"}`;
    setFormData((prev) => ({ ...prev, [name]: formatted }));
    handleError(name as keyof EvaluationFormData);
  };

  const handleInterviewerChange = (
    prefix: string,
    field: keyof Interviewer,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [prefix]: { ...(prev as any)[prefix], [field]: value },
    }));
    handleError(`${prefix}.${field}`);
  };

  return (
    <div className="pt-3">
      <p
        className="fw-bold mb-3"
        style={{
          // fontSize: "0.95rem",
          color: "#111827",
          textTransform: "uppercase",
        }}
      >
        Evaluation Sheet:
      </p>

      <RatingScale />

      {interviewerBlocks.map((b) => (
        <Eval_InterviewerBlock
          key={b.prefix}
          label={b.label}
          prefix={b.prefix}
          formData={formData[b.prefix]}
          errors={(errors as any)[b.prefix]}
          onChange={handleInterviewerChange}
          handleDateChange={handleDateChange}
        />
      ))}
    </div>
  );
};

export default EvaluationSheet;
